/**
 * @file        src/pages/customer/CustomerList.jsx
 * @description Customer List — B2C Customers (users.role='b2c_user') and B2B
 *              Companies (business_accounts, grouped: one company → many
 *              users.role IN ('b2b_owner','b2b_user') via users.business_id)
 *              as two tabs on one page. B2B rows expand in place to list that
 *              company's users (read-only here — CRUD for accounts/employees
 *              stays on Account Management → Corporate Accounts).
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (Customer Management), B2C only.
 *   - 2026-08-04 Claude — Added a B2B Companies tab, grouped by company with
 *                          expandable employee rows (Customer List request).
 *   - 2026-09-15 Claude — B2C tab is now read-only apart from Enable/Disable.
 *                          "Add New Customer", the row Edit link and the row
 *                          Delete button are gone, and so are their API routes
 *                          (see admin_backend routes/customers.routes.js for
 *                          the reasoning): customers self-register on the
 *                          storefront, an admin-edited email silently moves the
 *                          login that customer signs in with, and NO customer
 *                          can be deleted — bookings, payments and wallet
 *                          ledger rows point at them. Disable is the reversible
 *                          way to take an account out of service, and it is
 *                          enforced at login AND at checkout, not just here.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

const TABS = [
  { key: 'b2c', label: 'B2C Customers' },
  { key: 'b2b', label: 'B2B Companies' },
];

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CustomerList() {
  const [tab, setTab] = useState('b2c');

  return (
    <>
      <PageHead
        title="Customer List"
        subtitle={tab === 'b2c' ? 'Customer Management · registered B2C customers' : 'Customer Management · B2B companies and their users'}
        actions={tab === 'b2b'
          ? <Link to="/account/corporate-accounts" className="btn primary"><Icon name="plus" /> Manage Corporate Accounts</Link>
          : null}
      />

      <div className="seg" style={{ marginBottom: 14, width: 'fit-content' }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? 'active' : undefined} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'b2c' ? <B2CTab /> : <B2BTab />}
    </>
  );
}

function B2CTab() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.customers.list(params),
    queryFn: () => get('/customers', { params }),
    placeholderData: keepPreviousData,
  });

  const statusMut = useMutation({
    mutationFn: ({ id, isActive }) => patch(`/customers/${id}/status`, { is_active: isActive }),
    onSuccess: (_res, { isActive }) => {
      toast.ok(isActive ? 'Enabled' : 'Disabled',
        isActive ? 'Customer can sign in and book again.' : 'Customer can no longer sign in or book.');
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not update status'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  // Disabling is reversible but it does lock a real person out of a live
  // account mid-journey, so it is confirmed. Re-enabling is not destructive and
  // goes straight through.
  async function onToggleStatus(row) {
    const isActive = !!row.is_active;
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email;
    if (isActive && !(await confirm({
      title: 'Disable customer',
      message: `Disable “${name}”? They will not be able to sign in or complete a booking until you enable them again. Their existing bookings are unaffected.`,
      confirmLabel: 'Disable',
    }))) return;
    statusMut.mutate({ id: row.id, isActive: !isActive });
  }

  return (
    <article className="panel glass">
      <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
        <div className="field">
          <label>Name / Email / Mobile</label>
          <input placeholder="Search customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
      </form>

      <div className="table-wrap">
        {isLoading ? <TableSkeleton /> : (
          <table className="table">
            <thead>
              <tr><th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>City</th><th>Status</th><th>Joined</th><th>Action</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="empty">No customers yet.</td></tr>
              ) : rows.map((c, idx) => (
                <tr key={c.id}>
                  <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                  <td><b>{`${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}</b></td>
                  <td>{c.email || <span className="muted">—</span>}</td>
                  <td className="mono">{c.mobile || '—'}</td>
                  <td>{c.city || <span className="muted">—</span>}</td>
                  <td><Chip tone={c.is_active ? 'ok' : 'rej'}>{c.is_active ? 'Active' : 'Inactive'}</Chip></td>
                  <td>{fmtDate(c.created_at)}</td>
                  <td className="row-act">
                    <button
                      type="button"
                      className={`btn xs ${c.is_active ? 'ghost' : 'primary'}`}
                      onClick={() => onToggleStatus(c)}
                      disabled={statusMut.isPending}
                    >
                      {c.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </article>
  );
}

function B2BTab() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.businessAccounts.list(params),
    queryFn: () => get('/business-accounts', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function toggle(id) {
    setExpandedId((cur) => (cur === id ? null : id));
  }

  return (
    <article className="panel glass">
      <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
        <div className="field">
          <label>Company / Code / Email / GSTIN</label>
          <input placeholder="Search company…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
      </form>

      <div className="table-wrap">
        {isLoading ? <TableSkeleton /> : (
          <table className="table">
            <thead>
              <tr><th>#</th><th>Company</th><th>Contact</th><th>Status</th><th>Users</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="empty">No B2B companies yet.</td></tr>
              ) : rows.map((b, idx) => (
                <CompanyRows
                  key={b.id}
                  account={b}
                  index={(page - 1) * pageSize + idx + 1}
                  expanded={expandedId === b.id}
                  onToggle={() => toggle(b.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </article>
  );
}

function CompanyRows({ account, index, expanded, onToggle }) {
  return (
    <>
      <tr>
        <td className="mono">{index}</td>
        <td><b>{account.name}</b><small className="muted db">{account.agent_code || ''}</small></td>
        <td>{account.contact_email || <span className="muted">—</span>}<small className="muted db">{account.contact_phone || ''}</small></td>
        <td><Chip tone={account.status === 'active' ? 'ok' : account.status === 'suspended' ? 'rej' : 'pend'}>{account.status}</Chip></td>
        <td>
          <button type="button" className="btn xs ghost" onClick={onToggle}>
            <Icon name="chev" className={expanded ? 'rot-90' : undefined} /> {expanded ? 'Hide users' : 'View users'}
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={5}>
            <EmployeesSubTable accountId={account.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function EmployeesSubTable({ accountId }) {
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data, isLoading } = useQuery({
    queryKey: qk.businessAccounts.employees(accountId, { page, pageSize }),
    queryFn: () => get(`/business-accounts/${accountId}/users`, { params: { page, pageSize } }),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <div className="table-wrap" style={{ margin: '4px 0 8px' }}>
      {isLoading ? <TableSkeleton /> : (
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Status</th><th>Joined</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="empty">No users under this company yet.</td></tr>
            ) : rows.map((u) => (
              <tr key={u.id}>
                <td><b>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</b></td>
                <td>{u.email || <span className="muted">—</span>}</td>
                <td className="mono">{u.mobile || '—'}</td>
                <td>{u.role === 'b2b_owner' ? 'Owner' : 'Employee'}</td>
                <td><Chip tone={Number(u.is_active) ? 'ok' : 'rej'}>{Number(u.is_active) ? 'Active' : 'Inactive'}</Chip></td>
                <td>{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </div>
  );
}
